<template>
    <div>
        <loader v-if="showLoader" :size="50" :border-width="16" class="tw-my-8" />
        <!-- calendar -->
        <div v-else class="tw-mb-6">
            <div class="tw-w-full tw-box-border tw-my-4">
                <h4 class="tw-mb-4 tw-text-xl tw-font-semibold tw-leading-6 tw-text-center">
                    {{ translate('calendar.title') }}
                </h4>
            </div>
            <div class="calendar-wrapper tw-mb-8" id="calendar-wrapper">
                <div class="calendar table-container">
                    <table class="table is-bordered">
                        <!-- The name of the current month and the arrows to change -->
                        <thead>
                            <tr>
                                <th colspan="1" class="prev tw-align-middle" @click.prevent="previousMonth">
                                    <svg
                                        class="tw-block tw-mx-auto"
                                        width="12"
                                        height="12"
                                        viewBox="0 0 12 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M0.585305 5.51038C0.582718 5.51279 0.580145 5.51523 0.577584 5.51768C0.258593 5.82338 0.258597 6.31902 0.577591 6.62472L5.19832 11.0529C5.51731 11.3586 6.0345 11.3586 6.35349 11.0529C6.67248 10.7472 6.67248 10.2516 6.35348 9.94588L2.3104 6.07126L6.35355 2.19657C6.67254 1.89087 6.67254 1.39523 6.35355 1.08953C6.03455 0.783827 5.51736 0.783824 5.19837 1.08952L0.585305 5.51038Z"
                                            fill="#135958"
                                        />
                                    </svg>
                                </th>
                                <th colspan="5" class="tw-text-sm tw-uppercase tw-font-medium tw-align-middle">
                                    {{ translatedCurrentMonth }} {{ currentYear }}
                                </th>
                                <th colspan="1" class="next tw-align-middle" @click.prevent="nextMonth">
                                    <svg
                                        class="tw-block tw-mx-auto"
                                        width="7"
                                        height="12"
                                        viewBox="0 0 7 12"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            clip-rule="evenodd"
                                            d="M6.17251 5.51038C6.17509 5.51279 6.17767 5.51523 6.18023 5.51768C6.49922 5.82338 6.49922 6.31902 6.18022 6.62472L1.5595 11.0529C1.2405 11.3586 0.723314 11.3586 0.404324 11.0529C0.0853333 10.7472 0.0853365 10.2516 0.404331 9.94588L4.44741 6.07126L0.404259 2.19657C0.0852686 1.89087 0.085272 1.39523 0.404266 1.08953C0.723261 0.783827 1.24045 0.783824 1.55944 1.08952L6.17251 5.51038Z"
                                            fill="#135958"
                                        />
                                    </svg>
                                </th>
                            </tr>
                            <tr>
                                <th
                                    v-for="day in translatedDays"
                                    :key="day"
                                    class="tw-text-xs tw-uppercase tw-font-medium"
                                >
                                    {{ day.substring(0, 3) }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- days of the month -->
                            <tr v-for="(week, idx) in grid" :key="idx">
                                <td
                                    v-for="date in week"
                                    class="date"
                                    :key="formatDate(date)"
                                    @click.prevent="setDate(date)"
                                    :class="dateClasses(date)"
                                >
                                    {{ date.dayOfMonth() }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <!-- end calendar -->
        <fieldset class="tw-mb-6">
            <button class="btn btn-md btn-tertiary btn-fw" v-show="selectedDate" @click.prevent="next">
                {{ translate('calendar.confirm') }}
            </button>
        </fieldset>
        <div
            class="tw-text-xs tw-leading-tight tw-text-center tw-border tw-border-solid tw-border-blue tw-rounded-4 tw-p-2 tw-text-dark-grey support-message"
            v-html="translate('booking.info', { phone: phoneLink, email: emailLink })"
        ></div>
    </div>
</template>

<script>
import {
    LocalDate,
    LocalTime,
    ChronoUnit,
    DateTimeFormatter,
    DayOfWeek,
    TemporalAdjusters,
    ZoneId,
    ZonedDateTime,
} from '@js-joda/core';
import '@js-joda/timezone';
import i18n from '@js/vue/mixins/i18n';
import Loader from './shared/Loader.vue';
import { BOOKING_VIEWS } from '@js/vue/booking-views.js';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { trackBookingDateSelect } from '@js/vue/dataLayerUtils.js';
import { contactInfo } from '@js/vue/contact-info.js';

export default {
    name: 'Calendar',
    inject: ['lang', 'translations'],
    mixins: [i18n],
    emits: ['dateSelected', 'changeView'],
    components: {
        Loader,
    },
    props: {
        // optional
        dateFormat: {
            type: String,
            required: false,
            default: 'yyyy-MM-dd',
        },
        navigatePast: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            startDate: LocalDate.now(),
            FETCH_RANGE: 3,
            currentDate: null,
            formatPattern: DateTimeFormatter.ofPattern(this.dateFormat),
            selectedDate: null,
            showLoader: true,
            BOOKING_VIEWS,
            phoneLink: `<a class="tw-whitespace-nowrap tw-underline" href="tel:${contactInfo.phoneNumber}">${contactInfo.phoneNumber}</a>`,
            emailLink: `<a class="tw-whitespace-nowrap tw-underline" href="mailto:${contactInfo.emailAddress}">${contactInfo.emailAddress}</a>`,
        };
    },
    async created() {
        // date already selected
        if (this.date) this.initializeBookingDates();
        // otherwise initialize calendar - fetch availabilities with specified range
        this.initializeCalendar();
    },
    computed: {
        ...mapGetters('booking', [
            'getServiceId',
            'getServiceTitle',
            'getAdvanceHours',
            'getAvailabilities',
            'getLastFetchedDate',
            'getSelectedDate',
            'getDate',
        ]),
        serviceId() {
            return this.getServiceId;
        },
        serviceTitle() {
            return this.getServiceTitle;
        },
        advanceHours() {
            return this.getAdvanceHours;
        },
        grid() {
            return this.getCalendarMatrix(this.currentDate);
        },
        today() {
            return LocalDate.now();
        },
        currentYear() {
            return this.currentDate.year();
        },
        currentMonth() {
            return this.currentDate.month().value();
        },
        translatedCurrentMonth() {
            const month = this.currentDate.month().value();
            return this.translations.calendar.monthTranslations[lang][month - 1];
        },
        translatedDays() {
            return this.translations.calendar.dayTranslations[lang];
        },
        value() {
            return this.selectedDate;
        },
        availabilities() {
            return this.getAvailabilities;
        },
        lastFetchedDate() {
            return this.getLastFetchedDate;
        },
        availableDates() {
            return this.availabilities
                .filter((availability) => this.generateTimes(availability).length > 0)
                .map((availability) => availability.date);
        },
        date() {
            return this.getSelectedDate;
        },
    },
    methods: {
        ...mapActions('booking', ['fetchAvailabilities']),
        ...mapMutations('booking', ['SET_DATE', 'SET_AVAILABLE_TIMES']),
        async initializeBookingDates() {
            this.selectedDate = this.date;
            const date = LocalDate.parse(this.selectedDate);
            this.currentDate = this.startDate;
            const end = date.with(TemporalAdjusters.lastDayOfMonth()); // adjust to end of the month
            await this.loadBookingTimes(this.currentDate, end);
            this.setDate(date);
        },
        async initializeCalendar() {
            this.currentDate = this.startDate;
            const toDate = this.startDate.plusMonths(this.FETCH_RANGE);
            await this.loadBookingTimes(this.currentDate, toDate);
        },
        // 1. Create a calendar and change months
        getCalendarMatrix(date) {
            const calendarMatrix = [];

            let startDay = date.with(TemporalAdjusters.firstDayOfMonth());
            let lastDay = date.with(TemporalAdjusters.lastDayOfMonth());

            // If the month didn't start on a Monday,
            // start from the last Monday of the previous month
            if (startDay.dayOfWeek().value() !== 1) {
                startDay = startDay.with(TemporalAdjusters.previous(DayOfWeek.MONDAY));
            }

            // If the month didn't end on a Sunday,
            // end on the following Sunday in the next month
            if (lastDay.dayOfWeek().value() !== 7) {
                lastDay = lastDay.with(TemporalAdjusters.next(DayOfWeek.SUNDAY));
            }

            while (startDay.until(lastDay, ChronoUnit.DAYS) >= 0) {
                const endWeekDay = startDay.plusWeeks(1);
                const week = this.getDatesBetweenInclusive(startDay, endWeekDay);
                calendarMatrix.push(week);
                startDay = endWeekDay;
            }
            return calendarMatrix;
        },
        getDatesBetweenInclusive(start, end) {
            const dates = [];
            let iterator = start;
            while (end.isAfter(iterator)) {
                dates.push(iterator);
                iterator = iterator.plusDays(1);
            }
            return dates;
        },
        previousMonth() {
            const date = this.currentDate.minusMonths(1);
            if (this.navigatePast || this.startDate.compareTo(date) <= 0 || this.startDate.month() >= date.month()) {
                this.currentDate = date;
                this.monthChange(this.currentDate);
            }
        },
        nextMonth() {
            this.currentDate = this.currentDate.plusMonths(1);
            this.monthChange(this.currentDate);
        },
        // move to next or previous month based on selected date
        changeViewToDate(date) {
            this.currentDate = LocalDate.parse(date, this.formatPattern);
            this.monthChange(this.currentDate);
        },
        monthChange(currentCalendarDate) {
            const monthDiff = this.lastFetchedDate.until(currentCalendarDate, ChronoUnit.MONTHS);
            if (monthDiff > -1) {
                const range = monthDiff + this.FETCH_RANGE;
                const fromDate = this.lastFetchedDate.plusDays(1);
                const toDate = this.lastFetchedDate.plusMonths(range);
                this.loadBookingTimes(fromDate, toDate);
            }
        },
        // 2. Fetch available dates within range
        async loadBookingTimes(start, end) {
            this.showLoader = true;
            try {
                await this.fetchAvailabilities({ serviceId: this.serviceId, start, end });
                this.showLoader = false;
            } catch (error) {
                console.error('An error occurred while loading booking times:', error);
                this.showLoader = false;
            }
        },
        findAvailabilityByDate(date) {
            return this.availabilities.find((a) => a.date === date);
        },
        // Part for selected date:
        // Update the selected date and save state to store
        // to the month of the selected date and emit events for parent components
        setDate(date) {
            this.selectedDate = this.formatDate(date);
            this.changeViewToDate(this.selectedDate);
            this.SET_DATE(this.selectedDate);
            this.SET_AVAILABLE_TIMES(this.getAvailableTimes());
            this.$emit('dateSelected', this.selectedDate);
        },
        // change calendar view
        next() {
            trackBookingDateSelect(this.serviceTitle);
            this.$emit('changeView', BOOKING_VIEWS.TIME_PICKER);
        },
        getAvailableTimes() {
            const availability = this.findAvailabilityByDate(this.selectedDate);
            return availability ? this.generateTimes(availability) : [];
        },
        isSelected(date) {
            return this.value && date.equals(this.parseValue(this.value));
        },
        // 3. Check available times for specific date
        generateTimes(availability) {
            const dateToCheck = LocalDate.parse(availability.date);
            let zagrebDateTimeNow = ZonedDateTime.now(ZoneId.of('Europe/Zagreb')).plusHours(this.advanceHours);
            const cannotBeBookedBeforeDate = zagrebDateTimeNow.toLocalDate();
            const cannotBeBookedBeforeTime = zagrebDateTimeNow.toLocalTime();

            // there are no times if the date is before cannotBeBookedBeforeDate
            if (dateToCheck.isBefore(cannotBeBookedBeforeDate)) {
                return [];
            }
            // if the date is equal to cannotBeBookedBeforeDate check available times
            if (dateToCheck.isEqual(cannotBeBookedBeforeDate)) {
                return availability.times.filter((time) => {
                    // display only times after cannotBeBookedBeforeTime
                    return !LocalTime.parse(time).isBefore(cannotBeBookedBeforeTime);
                });
            }
            // date is after - display all times
            return availability.times;
        },
        formatDate(date) {
            return date.format(DateTimeFormatter.ofPattern(this.dateFormat));
        },
        parseValue(value) {
            return LocalDate.parse(value, this.formatPattern);
        },
        dateClasses(date) {
            return {
                today: this.today.equals(date),
                selected: this.isSelected(date),
                disabled: this.availableDates && this.availableDates.indexOf(this.formatDate(date)) === -1,
            };
        },
    },
};
</script>
